import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import OeeCard from "../components/Cards/OEE/OeeCard";
import { MachineStatusCard } from "../components/Cards/MachineStatus/MachineStatusCard";
import AssetDrawer from "../components/AssetDrawer";
import "../styles/MainDashboard.css";
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { FetchUserCards } from "../services/UserSettingService";
import { MachineCard } from "../components/Cards/Machine/MachineCard";
import { Introduction } from "../components/Introduction";
import { removeCard } from '../Slices/DashboardSlice';
import { TourButton } from '../components/TourButton';
import { useTourContext } from '../contexts/TourContext';
import { MainDashboardTour } from '../tours/MainDashboardTour';

export default function MainDashboard() {
    const cards = useAppSelector((state) => state.dashboardSlice.cards);
    const dispatch = useAppDispatch();
    const { registerTour } = useTourContext();
    const [cardsLoaded, setCardsLoaded] = useState<boolean>(false);
    const [introductionComplete, setIntroductionComplete] = useState<boolean>(() => {
        const savedValue = sessionStorage.getItem('introductionComplete');
        return savedValue !== null ? JSON.parse(savedValue) : false;
    });

    // Register the tour
    useEffect(() => {
        registerTour(MainDashboardTour);
    }, [registerTour]);

    useEffect(() => {
        sessionStorage.setItem('introductionComplete', JSON.stringify(introductionComplete));
    }, [introductionComplete]);

    useEffect(() => {
        (async () => {
            if (cards.length === 0) {
                var fetchedCards = await FetchUserCards();
                fetchedCards && fetchedCards.length > 0 && fetchedCards.forEach((card) => {
                    card && dispatch({
                        type: "cards/addCardWithoutSaving", payload:
                            { type: card.type, elementGuid: card.elementGuid, name: card.name }
                    });
                });
            }
            setCardsLoaded(true);
        })();
    }, [dispatch]);

    const handleDragEnd = useCallback((result: any) => {
        const { source, destination } = result;
        if (!destination) return;

        const updatedCards = Array.from(cards);
        const [removed] = updatedCards.splice(source.index, 1);
        updatedCards.splice(destination.index, 0, removed);

        const orderedGuids = updatedCards.map(card => card.elementGuid);

        dispatch({
            type: "cards/reorderCards",
            payload: orderedGuids
        });
    }, [cards, dispatch]);

    const cardElements: JSX.Element[] = cards.map((card) => {
        switch (card.type) {
            case "oee":
                return <OeeCard key={card.elementGuid + card.type} {...card} onDismiss={() => handleCardDismiss(card.elementGuid, card.type)} />;
            case "mstatus":
                return <MachineStatusCard key={card.elementGuid + card.type} {...card} onDismiss={() => handleCardDismiss(card.elementGuid, card.type)} />;
            case "machine":
                return <MachineCard key={card.elementGuid + card.type} {...card} onDismiss={() => handleCardDismiss(card.elementGuid, card.type)} />;
            default:
                console.error("Unknown card type: " + card.type);
                return null;
        }
    }).filter((card): card is JSX.Element => card !== null);

    const handleCardDismiss = (cardGuid: string, cardType: string) => {
        dispatch(removeCard({ eguid: cardGuid, type: cardType }));
    };

    const showIntroduction = cards.length === 0 && cardsLoaded && !introductionComplete;

    return (
        <DragDropContext
            onDragEnd={handleDragEnd}
        >
            <div className="">
                <div className="fixed inset-0 bg-cover bg-center bg-light dark:bg-dark -z-10" />

                {/* Tour Button */}
                <TourButton
                    tourId="main-dashboard"
                    className="fixed bottom-4 right-12 z-50"
                    label="Tour"
                />

                <div className="flex overflow-x-hidden px-0 pr-10 pt-0">
                    <AssetDrawer startOpen={cardElements.length === 0 && cardsLoaded} className="fixed md:relative" />
                    <div className="flex flex-col flex-grow pl-10 md:pl-4">
                        <Droppable droppableId="droppable" direction="horizontal">

                            {(provided) => (
                                <div
                                    className="grid grid-cols-[repeat(auto-fill,minmax(20rem,1fr))] gap-4 mb-6"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    data-tour="cards-grid"
                                >
                                    {cardElements.map((card, index) => (
                                        <Draggable key={card.key ?? ''} draggableId={card.key ?? ''} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef}{...provided.draggableProps} className="card-container relative">
                                                    <div className="absolute bg-red-100  h-14 w-10/12  mt-2 z-10 "
                                                        {...provided.dragHandleProps} />

                                                    <div className="">
                                                        {card}
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                </div>

                {showIntroduction && <Introduction isCompleted={() => setIntroductionComplete(true)} />}
            </div>
        </DragDropContext>
    );
}
